import { getDocs, collection, query, orderBy, doc, getDoc, deleteDoc, addDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db, auth } from '../../../../firebase/config';

export type InventoryItem = {
    id: string;
    itemName?: string;
    itemCode?: string;
    itemDescription?: string;
    brandName?: string;
    makeModel?: string;
    rates?: number;
    taxPercentage?: number;
    itemDate?: string;
    category?: string;
    quantity?: number;
    unit?: string;
    location?: string;
    branchCode?: string;
    branchName?: string;
    branchId?: string;
    status?: string;
    lastUpdated?: any;
    createdAt?: any;
    updatedAt?: any;
    createdBy?: string;
    companyId?: string;
    [key: string]: any;
};

export type ItemFormData = {
    itemName: string;
    itemDescription: string;
    brandName: string;
    makeModel: string;
    rates: number;
    taxPercentage: number;
    itemDate: string;
};

/**
 * Fetch company ID for the current user (PSO or Bank user)
 */
export async function fetchCompanyId(uid: string, isBankUser: boolean = false): Promise<string | null> {
    try {
        if (isBankUser) {
            // For bank users, check if uid is a bank document ID or if there's a bank with userId == uid
            const directBank = await getDoc(doc(db, "banks", uid));
            if (directBank.exists()) {
                return uid;
            }
            // Try query by userId field
            const banksSnap = await getDocs(collection(db, "banks"));
            for (const bankDoc of banksSnap.docs) {
                const bankData = bankDoc.data();
                if (bankData.userId === uid) {
                    return bankDoc.id;
                }
            }
            return null;
        } else {
            // For PSO users, find which bank has this PSO
            const banksSnap = await getDocs(collection(db, 'banks'));
            for (const bankDoc of banksSnap.docs) {
                const posDocRef = doc(db, 'banks', bankDoc.id, 'pos', uid);
                const posDoc = await getDoc(posDocRef);
                if (posDoc.exists()) {
                    return bankDoc.id;
                }
            }
            return null;
        }
    } catch (error) {
        console.error('Error fetching company ID:', error);
        return null;
    }
}

/**
 * Fetch all inventory items for a company
 */
export async function fetchInventoryItems(companyId: string): Promise<InventoryItem[]> {
    if (!companyId) {
        console.log('No company ID provided');
        return [];
    }

    try {
        const itemsSnap = await getDocs(
            query(
                collection(db, "banks", companyId, "inventory"),
                orderBy("createdAt", "desc")
            )
        );
        
        const itemsList = itemsSnap.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        })) as InventoryItem[];

        return itemsList;
    } catch (error) {
        console.error('Error fetching inventory items:', error);
        throw error;
    }
}

/**
 * Create a new inventory item
 */
export async function createInventoryItem(
    companyId: string,
    itemData: Partial<InventoryItem>
): Promise<string> {
    const uid = auth.currentUser?.uid;
    if (!uid || !companyId) {
        throw new Error('User ID or company ID not found');
    }

    try {
        const newItemData = {
            ...itemData,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            createdBy: uid,
            companyId: companyId,
        };

        const docRef = await addDoc(collection(db, 'banks', companyId, 'inventory'), newItemData);
        return docRef.id;
    } catch (error) {
        console.error('Error creating inventory item:', error);
        throw error;
    }
}

/**
 * Update an existing inventory item
 */
export async function updateInventoryItem(
    companyId: string,
    itemId: string,
    updateData: Partial<InventoryItem>
): Promise<void> {
    if (!companyId || !itemId) {
        throw new Error('Company ID or item ID not provided');
    }

    try {
        await updateDoc(doc(db, 'banks', companyId, 'inventory', itemId), {
            ...updateData,
            updatedAt: serverTimestamp(),
        });
    } catch (error) {
        console.error('Error updating inventory item:', error);
        throw error;
    }
}

/**
 * Delete an inventory item
 */
export async function deleteInventoryItem(companyId: string, itemId: string): Promise<void> {
    if (!companyId || !itemId) {
        throw new Error('Company ID or item ID not provided');
    }

    try {
        await deleteDoc(doc(db, 'banks', companyId, 'inventory', itemId));
    } catch (error) {
        console.error('Error deleting inventory item:', error);
        throw error;
    }
}

